import { Button } from 'nhsuk-react-components';
import { JSX, MouseEventHandler, useState } from 'react';
import useSendReportToNotify from 'queries/mutate/sendReportToNotify';
import Confirm from 'components/Confirm';
import Alert from 'components/Alert';
import { API_SEND_TO_NOTIFY_ARCHIVED_ERROR, API_SEND_REPORT_TO_NOTIFY_ERROR } from 'constants/errorMessages';

interface ISendToNotifyProps {
  teamId: string,
  campaignId: string,
  iterationId: string,
  batchId: string;
}

function SendToNotify({
  campaignId,
  iterationId,
  teamId,
  batchId,
}: ISendToNotifyProps) {
  const [isShowingSendToNotifyDialog, setIsShowingSendToNotifyDialog] = useState(false);
  const sendReportToNotify = useSendReportToNotify();
  const [errorTitle, setErrorTitle] = useState<string>('Cannot send iteration to NHS Notify');
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element | null>(null);
  const [errorButtonText, setErrorButtonText] = useState<string>('Close');

  const onClickSendToNotify: MouseEventHandler = () => {
    setIsShowingSendToNotifyDialog(true);
  };

  const abort = () => {
    setIsShowingSendToNotifyDialog(false);
  };

  const confirm = async () => {
    setIsShowingSendToNotifyDialog(false);
    try {
      await sendReportToNotify.mutateAsync({
        campaignId,
        iterationId,
        teamId,
        batchId,
      });
    } catch (e) {
      if (e instanceof Error && e.message.includes('stale')) {
        setErrorTitle('Cannot send iteration to NHS Notify');
        setErrorMessage(API_SEND_TO_NOTIFY_ARCHIVED_ERROR);
        setErrorButtonText('Close');
        return;
      }
      if (e instanceof Error && e.message === 'A selected cohort has been withdrawn') {
        setErrorTitle('Cohort(s) removed');
        setErrorMessage(
          <>
            <p>One or more of your cohorts is no longer available.</p>
            <p className="nhsuk-u-margin-bottom-0"><b>What you need to do next</b></p>
            <ol className="nhsuk-u-margin-bottom-0">
              <li className="nhsuk-u-margin-bottom-0">Delete any cohorts which are &apos;removed from team&apos;.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your cohorts and change if necessary.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your rules, as they may be affected, and change if necessary.</li>
            </ol>
          </>,
        );
        setErrorButtonText('I understand');
        return;
      }

      setErrorTitle('Cannot send iteration to NHS Notify');
      setErrorMessage(API_SEND_REPORT_TO_NOTIFY_ERROR);
      setErrorButtonText('Close');
    }
  };

  const isSubmitting = sendReportToNotify.isPending;

  return (
    <>
      <Button
        onClick={onClickSendToNotify}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Updating...' : 'Send Iteration to NHS Notify'}
      </Button>
      {isShowingSendToNotifyDialog && (
        <Confirm
          title="Send Iteration to NHS Notify"
          message="This action is irreversible and will prevent any future updates to the iteration."
          yesText="Yes, send to NHS Notify"
          noText="Cancel"
          onYes={confirm}
          onNo={abort}
        />
      )}
      {errorMessage && (
        <div className="nhsuk-u-margin-top-3 nhsuk-u-width-full">
          <Alert
            title={errorTitle}
            message={errorMessage}
            buttonText={errorButtonText}
            onClose={() => setErrorMessage(null)}
          />
        </div>
      )}
    </>
  );
}
export default SendToNotify;
